import { useState } from 'react';
import toast from 'react-hot-toast';
import { Button, Input } from '@novaquantis/design-system';
import { useConnectAlpacaMutation } from '../../queries/retailAccounts';
import { LIVE_ENDPOINT, PAPER_ENDPOINT } from '../../onboarding/onboardingSteps';

type Props = {
  onLinked?: () => void | Promise<void>;
  submitLabel?: string;
  submitting?: boolean;
};

const LiveEndpointIcon = () => (
  <svg
    className="shrink-0"
    width="16"
    height="16"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden
  >
    <line x1="12" y1="2" x2="12" y2="22" />
    <path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6" />
  </svg>
);

const PaperEndpointIcon = () => (
  <svg
    className="shrink-0"
    width="16"
    height="16"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden
  >
    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
    <polyline points="14 2 14 8 20 8" />
    <line x1="8" y1="13" x2="16" y2="13" />
    <line x1="8" y1="17" x2="13" y2="17" />
  </svg>
);

const ConnectAlpacaForm = ({
  onLinked,
  submitLabel = 'Link Alpaca Account',
  submitting = false,
}: Props) => {
  const [form, setForm] = useState({
    apiKey: '',
    secretKey: '',
    endpoint: LIVE_ENDPOINT,
    nickname: '',
  });

  const connectMutation = useConnectAlpacaMutation();
  const isBusy = connectMutation.isPending || submitting;
  const isPaperEndpoint = form.endpoint.toLowerCase().includes('paper');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      const { data } = await connectMutation.mutateAsync({
        broker: 'alpaca',
        endpoint: form.endpoint.trim(),
        isPaper: form.endpoint.toLowerCase().includes('paper'),
        ...(form.nickname.trim() && { nickname: form.nickname.trim() }),
        credentials: {
          apiKey: form.apiKey.trim(),
          secretKey: form.secretKey.trim(),
        },
      });
      toast.success(data.message || 'Alpaca account linked');
      setForm((prev) => ({ ...prev, apiKey: '', secretKey: '', nickname: '' }));
      await onLinked?.();
    } catch (error: unknown) {
      toast.error(
        (error as { response?: { data?: { error?: string } } }).response?.data?.error ||
          'Failed to link Alpaca account',
      );
    }
  };

  const presetBase =
    'inline-flex min-h-[42px] cursor-pointer items-center justify-center gap-1 whitespace-nowrap rounded-md border border-transparent bg-transparent px-3 text-sm font-semibold leading-tight transition-colors';
  const liveClass = !isPaperEndpoint
    ? 'text-secondary border-secondary/55 bg-secondary/16 shadow-[0_0_0_1px_rgba(247,148,29,0.12)]'
    : 'text-secondary/85 border-secondary/28 bg-secondary/6 hover:bg-secondary/12 hover:text-secondary';
  const paperClass = isPaperEndpoint
    ? 'text-[color:var(--nq-blue-bright)] border-primary/55 bg-primary/14 shadow-[0_0_0_1px_rgba(37,180,224,0.12)]'
    : 'text-primary/85 border-primary/28 bg-primary/6 hover:bg-primary/12 hover:text-[color:var(--nq-blue-bright)]';

  return (
    <form onSubmit={handleSubmit}>
      <p className="m-0 mb-3 text-sm leading-normal text-muted-foreground">
        Use API keys from the same environment as your endpoint (paper vs live).
      </p>
      <div className="mb-4 grid gap-2 [grid-template-columns:repeat(auto-fit,minmax(220px,1fr))]">
        <Input
          label="API Key"
          name="apiKey"
          value={form.apiKey}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
            setForm((p) => ({ ...p, apiKey: e.target.value }))
          }
          autoComplete="off"
          required
          variant="onDark"
        />
        <Input
          label="Secret Key"
          name="secretKey"
          type="password"
          value={form.secretKey}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
            setForm((p) => ({ ...p, secretKey: e.target.value }))
          }
          autoComplete="off"
          required
          variant="onDark"
        />
        <Input
          label="Account Name"
          name="nickname"
          value={form.nickname}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
            setForm((p) => ({ ...p, nickname: e.target.value }))
          }
          placeholder="e.g. My Live Account"
          autoComplete="off"
          variant="onDark"
        />
      </div>

      <div className="mb-4">
        <label
          htmlFor="connect-alpaca-endpoint"
          className="mb-1 block font-sans text-sm font-semibold tracking-[0.02em] text-[color:var(--text-secondary)]"
        >
          Alpaca API Endpoint
          <span className="font-bold text-[color:var(--nq-blue-bright)]" aria-hidden>
            {' '}
            *
          </span>
        </label>
        <div className="flex flex-wrap items-stretch gap-2">
          <input
            id="connect-alpaca-endpoint"
            className="box-border min-w-[min(100%,16rem)] max-w-full flex-[1_1_14rem] rounded-md border border-[color:var(--border-slate)] bg-[color:var(--nq-surface-raised)] px-3 py-[11px] font-mono text-sm text-foreground outline-none transition-[border-color,box-shadow,background] placeholder:text-muted-foreground hover:border-[color:rgba(255,255,255,0.18)] hover:bg-[color:rgba(255,255,255,0.06)] focus:border-primary/50 focus:bg-primary/6 focus:shadow-[0_0_0_2px_rgba(37,180,224,0.15)] sm:max-w-[50%]"
            name="endpoint"
            value={form.endpoint}
            onChange={(e) => setForm((p) => ({ ...p, endpoint: e.target.value }))}
            autoComplete="off"
            required
            spellCheck={false}
          />
          <div
            className="flex flex-1 shrink-0 flex-wrap items-stretch gap-1.5 sm:flex-none [&>button]:flex-1 sm:[&>button]:flex-none"
            role="group"
            aria-label="Endpoint environment"
          >
            <button
              type="button"
              className={`${presetBase} ${liveClass}`}
              onClick={() => setForm((p) => ({ ...p, endpoint: LIVE_ENDPOINT }))}
            >
              <LiveEndpointIcon />
              <span>Live</span>
            </button>
            <button
              type="button"
              className={`${presetBase} ${paperClass}`}
              onClick={() => setForm((p) => ({ ...p, endpoint: PAPER_ENDPOINT }))}
            >
              <PaperEndpointIcon />
              <span>Paper</span>
            </button>
          </div>
        </div>
      </div>

      <Button type="submit" disabled={isBusy} variant="glow" fullWidth>
        {isBusy ? 'Linking…' : submitLabel}
      </Button>
    </form>
  );
};

export default ConnectAlpacaForm;
